import type { QueryClient } from "@tanstack/react-query";
import type {
  AdminOrdersFilters,
  MarketplaceOrder,
  OrderScriptActivityAudience,
  OrderHandoffEmailPreviewPayload,
  PartnerOrdersQueryFilters,
  SentScriptsMap,
  TenantOrdersFilters,
  TenantOrdersPageResponse,
} from "@/features/marketplace/types/marketplace.types";
import {
  hasInFlightHandoffOrders,
  ORDER_HANDOFF_POLL_INTERVAL_MS,
} from "@/features/marketplace/lib/order-handoff-ux";

function orderScriptActivityKey(
  audience: OrderScriptActivityAudience,
  tenantScope: string,
  orderId: string,
  orderScriptId: string,
) {
  return [
    "marketplace",
    "order-script-activity",
    audience,
    tenantScope,
    orderId,
    orderScriptId,
  ] as const;
}

export const marketplaceKeys = {
  all: ["marketplace"] as const,
  vendors: (tenantId: string) => ["marketplace", "vendors", tenantId] as const,
  vendorDetails: (vendorId: string) =>
    ["marketplace", "vendor", vendorId, "details"] as const,
  connections: (tenantId: string) =>
    ["marketplace", "connections", tenantId] as const,
  orders: (tenantId: string) => ["marketplace", "orders", tenantId] as const,
  order: (
    tenantId: string,
    orderId: string,
    fulfillmentGroup?: TenantOrdersFilters["fulfillmentGroup"],
  ) =>
    ["marketplace", "orders", tenantId, orderId, fulfillmentGroup] as const,
  ordersForSet: (tenantId: string, opportunitySetId: string) =>
    ["marketplace", "orders", tenantId, "set", opportunitySetId] as const,
  /** Opportunity Hub "Sent to vendor" tab — one key per selected-set combination. */
  ordersForSets: (tenantId: string, opportunitySetIds: readonly string[]) =>
    ["marketplace", "orders", tenantId, "sets", [...opportunitySetIds].sort().join(",")] as const,
  sentScripts: (tenantId: string) =>
    ["marketplace", "sent-scripts", tenantId] as const,
  orderHandoffDefaults: (tenantId: string, tenantVendorId: string) =>
    [
      "marketplace",
      "order-handoff-defaults",
      tenantId,
      tenantVendorId,
    ] as const,
  orderHandoffEmailPreview: (payload: OrderHandoffEmailPreviewPayload) =>
    ["marketplace", "order-handoff-email-preview", payload] as const,
  adminVendors: ["marketplace", "admin", "vendors"] as const,
  adminPendingVendors: ["marketplace", "admin", "vendors", "pending"] as const,
  adminPendingHandoffs: [
    "marketplace",
    "admin",
    "orders",
    "pending-handoffs",
  ] as const,
  adminVendor: (vendorId: string) =>
    ["marketplace", "admin", "vendor", vendorId] as const,
  adminOrderImport: (tenantId: string, importId: string) =>
    ["marketplace", "admin", "order-import", tenantId, importId] as const,
  adminTeamRecipients: ["marketplace", "admin", "team-recipients"] as const,
  adminNotificationSettings: [
    "marketplace",
    "admin",
    "notification-settings",
  ] as const,
  adminOrdersRoot: ["marketplace", "admin", "orders"] as const,
  adminOrders: (filters: AdminOrdersFilters) =>
    ["marketplace", "admin", "orders", filters] as const,
  adminOrderVendors: ["marketplace", "admin", "orders", "vendors"] as const,
  adminOrderPartnerOptions: (orderId: string) =>
    ["marketplace", "admin", "orders", orderId, "partner-options"] as const,
  partnerOrdersRoot: (partnerTenantId: string) =>
    ["marketplace", "partner", partnerTenantId, "orders"] as const,
  partnerOrders: (
    partnerTenantId: string,
    filters: PartnerOrdersQueryFilters,
  ) => ["marketplace", "partner", partnerTenantId, "orders", filters] as const,
  partnerOrder: (partnerTenantId: string, orderId: string) =>
    ["marketplace", "partner", partnerTenantId, "order", orderId] as const,
  orderScriptActivity: (
    audience: OrderScriptActivityAudience,
    tenantScope: string,
    orderId: string,
    orderScriptId: string,
  ) => orderScriptActivityKey(audience, tenantScope, orderId, orderScriptId),
  partnerPreflightComments: (
    partnerTenantId: string,
    orderId: string,
    orderScriptId: string,
  ) =>
    orderScriptActivityKey("partner", partnerTenantId, orderId, orderScriptId),
  preflightComments: (
    audience: Exclude<OrderScriptActivityAudience, "partner">,
    tenantScope: string,
    orderId: string,
    orderScriptId: string,
  ) => orderScriptActivityKey(audience, tenantScope, orderId, orderScriptId),
} as const;

const MILLISECONDS_PER_MINUTE = 60_000;
const MARKETPLACE_STALE_TIME_MINUTES = 5;
export const MARKETPLACE_STALE_TIME_MS =
  MARKETPLACE_STALE_TIME_MINUTES * MILLISECONDS_PER_MINUTE;
export const PARTNER_ORDERS_POLL_INTERVAL_MS = 10_000;

export function inFlightHandoffRefetchInterval(query: {
  state: { data?: TenantOrdersPageResponse | MarketplaceOrder[] };
}) {
  const orders = Array.isArray(query.state.data)
    ? query.state.data
    : query.state.data?.orders;
  return hasInFlightHandoffOrders(orders)
    ? ORDER_HANDOFF_POLL_INTERVAL_MS
    : false;
}

export function inFlightSentScriptsRefetchInterval(query: {
  state: { data?: SentScriptsMap };
}) {
  return hasInFlightHandoffOrders(Object.values(query.state.data ?? {}))
    ? ORDER_HANDOFF_POLL_INTERVAL_MS
    : false;
}

export function invalidateMarketplaceOrders(
  queryClient: QueryClient,
  tenantId: string,
) {
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.orders(tenantId) });
  queryClient.invalidateQueries({
    queryKey: marketplaceKeys.sentScripts(tenantId),
  });
  queryClient.invalidateQueries({
    queryKey: marketplaceKeys.adminOrdersRoot,
  });
}

export function invalidatePartnerOrderQueries(
  queryClient: QueryClient,
  partnerTenantId: string,
  orderId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: marketplaceKeys.partnerOrder(partnerTenantId, orderId),
  });
  void queryClient.invalidateQueries({
    queryKey: marketplaceKeys.partnerOrdersRoot(partnerTenantId),
  });
}
